import { useCallback, useEffect, useState } from "react";
import AuthService from "../../base/services/authentication.service";
import { fundsService } from "./fundsService";
import { useFundsStore } from "./fundsStore";
import { mapTransactionToRow } from "./utils";

const EMPTY_OVERVIEW = Object.freeze({
   funds: 0,
   totalPurchased: 0,
   totalSpent: 0
});

/**
 * Owns all data the funds page renders: the overview totals, the formatted
 * transaction history and the organization's saved card. Exposes a `refresh`
 * callback the purchase flow calls to re-pull everything after a successful buy.
 *
 * The spinner is cleared only once all three requests have settled (via a
 * single Promise.allSettled), so it can't hide before the data lands.
 */
export function useFundsData() {
   const organizationId = AuthService.getOrganizationId();

   const [isLoading, setIsLoading] = useState(true);
   const [refreshToken, setRefreshToken] = useState(0);
   const [overview, setOverview] = useState(EMPTY_OVERVIEW);
   const [history, setHistory] = useState([]);
   const [savedCard, setSavedCard] = useState(null);

   const refresh = useCallback(() => setRefreshToken((token) => token + 1), []);

   useEffect(() => {
      let isActive = true;
      setIsLoading(true);

      const overviewRequest = fundsService.getOverview((res) => {
         if (!isActive) return;
         // Backend returns the balance under `credits`; the app calls it `funds`.
         const { credits, totalPurchased, totalSpent } = res.data ?? {};
         setOverview({
            funds: credits ?? 0,
            totalPurchased: totalPurchased ?? 0,
            totalSpent: totalSpent ?? 0
         });
         // Keep the header badge in step with what the page just fetched —
         // covers purchases, refunds and the 3DS redirect (all funnel through
         // `refresh`) without an extra request.
         useFundsStore.getState().syncFromOverview(res.data);
      });

      const historyRequest = fundsService.getTransactions((res) => {
         if (!isActive) return;
         setHistory((res.data ?? []).map(mapTransactionToRow));
      });

      const savedCardRequest = fundsService.getSavedCard(organizationId, (res) => {
         if (!isActive) return;
         setSavedCard(res.data);
      });

      Promise.allSettled([overviewRequest, historyRequest, savedCardRequest]).finally(() => {
         if (isActive) setIsLoading(false);
      });

      // Guard against a slow response resolving after the org changed / unmount.
      return () => {
         isActive = false;
      };
   }, [organizationId, refreshToken]);

   return { organizationId, isLoading, overview, history, savedCard, refresh };
}
